import {useState, useEffect, useRef} from 'react'
import {SectionConfig} from '../types'
import {STORAGE_KEYS} from '../constants'

const useSectionManager = (defaultSections: SectionConfig[]) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [sectionsOrder, setSectionsOrder] = useState<string[]>([])
  const [hiddenSections, setHiddenSections] = useState<string[]>([])
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current && defaultSections.length > 0) {
      const savedOrder = localStorage.getItem(STORAGE_KEYS.SECTIONS_ORDER)
      const savedHidden = localStorage.getItem(STORAGE_KEYS.HIDDEN_SECTIONS)

      if (savedOrder) {
        try {
          const parsedOrder: string[] = JSON.parse(savedOrder)
          
          const currentSectionIds = defaultSections.map(section => section.id)
          const validSavedOrder = parsedOrder.filter(id => currentSectionIds.includes(id))
          const newSections = currentSectionIds.filter(id => !parsedOrder.includes(id))
          
          setSectionsOrder([...validSavedOrder, ...newSections])
        } catch (error) {
          console.error('Error parsing sections order from localStorage:', error)
          setSectionsOrder(defaultSections.map(section => section.id))
        }
      } else {
        setSectionsOrder(defaultSections.map(section => section.id))
      }

      if (savedHidden) {
        try {
          const parsedHidden: string[] = JSON.parse(savedHidden)
         
          const currentSectionIds = defaultSections.map(section => section.id)
          const validHiddenSections = parsedHidden.filter(id => currentSectionIds.includes(id))
          setHiddenSections(validHiddenSections)
        } catch (error) {
          console.error('Error parsing hidden sections from localStorage:', error)
          setHiddenSections([])
        }
      }

      hasInitialized.current = true
    }
  }, [defaultSections])

  // Update localStorage whenever sectionsOrder changes (but not on initial load)
  useEffect(() => {
    if (hasInitialized.current && sectionsOrder.length > 0) {
      localStorage.setItem(STORAGE_KEYS.SECTIONS_ORDER, JSON.stringify(sectionsOrder))
    }
  }, [sectionsOrder])

  // Update localStorage whenever hiddenSections changes (but not on initial load)
  useEffect(() => {
    if (hasInitialized.current) {
      localStorage.setItem(STORAGE_KEYS.HIDDEN_SECTIONS, JSON.stringify(hiddenSections))
    }
  }, [hiddenSections])

  const toggleEditMode = (): void => setIsEditMode(!isEditMode)

  const moveUp = (sectionId: string): void => {
    const currentIndex = sectionsOrder.indexOf(sectionId)
    if (currentIndex > 0) {
      const newOrder = [...sectionsOrder];
      [newOrder[currentIndex], newOrder[currentIndex - 1]] = [newOrder[currentIndex - 1], newOrder[currentIndex]]
      setSectionsOrder(newOrder)
    }
  }

  const moveDown = (sectionId: string): void => {
    const currentIndex = sectionsOrder.indexOf(sectionId)
    if (currentIndex < sectionsOrder.length - 1) {
      const newOrder = [...sectionsOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]]
      setSectionsOrder(newOrder)
    }
  }

  const toggleVisibility = (sectionId: string): void => {
    setHiddenSections(prev => {
      if (prev.includes(sectionId)) {
        return prev.filter(id => id !== sectionId)
      }
      return [...prev, sectionId]
    })
  }

  const resetSections = (): void => {
    // Reset to default order
    const defaultOrder = defaultSections.map(section => section.id)
    setSectionsOrder(defaultOrder)
    
    // Reset visibility (clear hidden sections)
    setHiddenSections([])
    
    // Update localStorage
    localStorage.setItem(STORAGE_KEYS.SECTIONS_ORDER, JSON.stringify(defaultOrder))
    localStorage.setItem(STORAGE_KEYS.HIDDEN_SECTIONS, JSON.stringify([]))
  }

  const getOrderedSections = (): SectionConfig[] => {
    if (sectionsOrder.length === 0) {
      return defaultSections
    }
    
    return sectionsOrder
      .map(id => defaultSections.find(section => section.id === id))
      .filter((section): section is SectionConfig => section !== undefined)
  }

  const getVisibleSections = (): SectionConfig[] => {
    if (isEditMode) {
      return getOrderedSections()
    }
    return getOrderedSections().filter(section => !hiddenSections.includes(section.id))
  }

  return {
    isEditMode,
    hiddenSections,
    sectionsOrder,
    toggleEditMode,
    moveUp,
    moveDown,
    toggleVisibility,
    getVisibleSections,
    resetSections
  }
}
export default useSectionManager